/**
 * Frontend API service for plan CRUD operations.
 *
 * Thin fetch wrappers that keep HTTP logic out of components.
 * All requests include credentials so session cookies are sent.
 */

import type { Corridor } from '../types/plan';

export interface PlanListItem {
  id: number;
  name: string;
  description: string | null;
  updated_at: string;
  intersection_count: number;
  plan_count: number;
  is_example: boolean;
}

export interface PlanRecord {
  id: number;
  name: string;
  description: string | null;
  /** Always Corridor format (API converts legacy on read) */
  plan_data: Corridor;
  created_at: string;
  updated_at: string;
  /** True for seeded example plans (no owner) */
  is_example?: boolean;
}

export interface TierLimits {
  maxCorridors: number;
  maxPlansPerCorridor: number;
  maxIntersectionsPerCorridor: number;
  pdfExport: boolean;
  synchroImport: boolean;
  smartAutoSequence: boolean;
}

/** Fetch public tier limits so copy never hard-codes numbers */
export async function fetchTiers(): Promise<{ free: TierLimits; pro: TierLimits }> {
  const res = await fetch(`/api/tiers`);
  if (!res.ok) throw new Error('Failed to fetch tiers');
  return res.json();
}

/** Fetch the list of saved plans (lightweight, no plan_data) */
export async function fetchPlanList(): Promise<PlanListItem[]> {
  const res = await fetch('/api/plans', { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to fetch plans: HTTP ${res.status}`);
  return res.json();
}

/** Fetch a single plan with full data */
export async function fetchPlan(id: number): Promise<PlanRecord> {
  const res = await fetch(`/api/plans/${id}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to fetch plan: HTTP ${res.status}`);
  return res.json();
}

/** Create a new plan in the database */
export async function createPlan(corridor: Corridor): Promise<PlanRecord> {
  const res = await fetch('/api/plans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      name: corridor.name,
      description: corridor.description || null,
      plan_data: corridor,
    }),
  });
  if (!res.ok) throw new Error(`Failed to save plan: HTTP ${res.status}`);
  return res.json();
}

/** Update an existing plan in the database */
export async function updatePlan(dbId: number, corridor: Corridor): Promise<PlanRecord> {
  const res = await fetch(`/api/plans/${dbId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      name: corridor.name,
      description: corridor.description || null,
      plan_data: corridor,
    }),
  });
  if (!res.ok) throw new Error(`Failed to update plan: HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Share API
// ---------------------------------------------------------------------------

export interface ShareStatus {
  share_token: string | null;
}

/** Get the share status of a plan */
export async function getShareStatus(dbId: number): Promise<ShareStatus> {
  const res = await fetch(`/api/plans/${dbId}/share`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to get share status: HTTP ${res.status}`);
  return res.json();
}

/** Enable sharing for a plan and get the share token */
export async function createShareLink(dbId: number): Promise<ShareStatus> {
  const res = await fetch(`/api/plans/${dbId}/share`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to create share link: HTTP ${res.status}`);
  return res.json();
}

/** Revoke sharing for a plan */
export async function revokeShareLink(dbId: number): Promise<void> {
  const res = await fetch(`/api/plans/${dbId}/share`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to revoke share link: HTTP ${res.status}`);
}

/** Fetch a shared plan by its share token (public, no auth required) */
export async function fetchSharedPlan(token: string): Promise<PlanRecord> {
  const res = await fetch(`/api/plans/share/${token}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error('Shared plan not found or link has expired');
    throw new Error(`Failed to fetch shared plan: HTTP ${res.status}`);
  }
  return res.json();
}
